import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppbarBase from "../../../../../components/common/appbar";
import { LuChevronLeft, LuXCircle } from "react-icons/lu";
import { MOBILE_CONTAINER } from "../../../../../utils/ui.constants";
import { useSelector } from "react-redux";
import { useOrderContext } from "../../../OrderPage";
import { numeralCustom } from "../../../../../utils/helpers";
import TotalLine from "../../../../../components/common/total-line";
import TienThu from "./TienThu";
import Pttt from "./Pttt";
import { asyncPostData, asyncPutData } from "../../../../../utils/httpRequests";
import { useAlertContext } from "../../../../../contexts/alert/AlertProvider";
import { cloneDeep, isArray } from "lodash";

type ChietKhauType = {
  tien_ck: number;
  ty_le_ck: number;
};
interface IPayment {
  open: boolean;
  onClose: () => void;
}

function Payment({ open, onClose }: IPayment) {
  const { showAlert } = useAlertContext();
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { currentHd } = useSelector((state: any) => state.order);
  const { giamGia, handleUpdateCurrentHd, saveHd } = useOrderContext();
  const [maVoucher, setMaVoucher] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isPercent, setIsPercent] = useState<boolean>(false);
  const [giaTriChietKhau, setGiaTriChietKhau] = useState<ChietKhauType>({
    tien_ck: 0,
    ty_le_ck: 0,
  });

  const handlePayment = async () => {
    const resp = await saveHd(5);
    if (!!resp) {
      onClose();
    }
  };

  const handleSaveCk = async () => {
    handleUpdateCurrentHd({
      tien_ck_hd: giaTriChietKhau.tien_ck,
      ty_le_ck_hd: giaTriChietKhau.ty_le_ck,
    });
  };

  const handleGiaTriChietKhauChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const tienHang = currentHd.t_tien_nt;
    let value = 0;
    if (isPercent) {
      value = +e.target.value;
      if (value > 100) {
        value = 100;
      }
      const tienCk = +((tienHang * value) / 100).toFixed(0);
      setGiaTriChietKhau({ tien_ck: tienCk, ty_le_ck: value });
    } else {
      value = numeralCustom(e.target.value).value() || 0;
      if (value > tienHang) {
        value = tienHang;
      }
      const tyLeCk = (value * 100) / tienHang;
      setGiaTriChietKhau({ tien_ck: value, ty_le_ck: tyLeCk });
    }
  };

  const applyVoucher = async () => {
    if (loading) return;
    setLoading(true);
    let resp;
    if (currentHd?.is_new) {
      resp = await asyncPostData({
        apiCode: "pbl_nh",
        token,
        idApp: app?._id,
        data: { ...currentHd, trang_thai: 1, evouchers: [{ ma: maVoucher }] },
      });
    } else {
      resp = await asyncPutData({
        apiCode: "pbl_nh",
        token,
        idApp: app?._id,
        id: currentHd?._id,
        data: { ...currentHd, evouchers: [{ ma: maVoucher }] },
      });
    }

    if (!resp || resp?.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi áp dụng voucher",
      });
      setLoading(false);
    }
    handleUpdateCurrentHd(resp?.data);
    setMaVoucher("");
    setLoading(false);
  };

  const deleteVoucher = async (maVoucher: string) => {
    const hdClone = cloneDeep(currentHd);
    const evouchers = (hdClone?.evouchers || []).filter(
      (item: any) => item.ma !== maVoucher
    );
    hdClone.evouchers = evouchers;
    hdClone.tien_evoucher = 0;

    const resp = await asyncPutData({
      apiCode: "pbl_nh",
      token,
      idApp: app?._id,
      id: currentHd?._id,
      data: hdClone,
    });
    if (!resp || resp?.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi hủy voucher",
      });
      setLoading(false);
    }
    handleUpdateCurrentHd(resp?.data);
  };

  useEffect(() => {
    setGiaTriChietKhau({
      tien_ck: currentHd?.tien_ck_hd || 0,
      ty_le_ck: currentHd?.ty_le_ck_hd || 0,
    });
  }, [currentHd?.tien_ck_hd, currentHd?.ty_le_ck_hd]);

  return (
    <Drawer
      title="Thanh toán"
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "100vw",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          backgroundColor: "background.paper",
        }}
      >
        <AppbarBase>
          <Stack
            sx={{ width: "100%", height: "100%" }}
            direction="row"
            alignItems="center"
            spacing="10px"
          >
            <IconButton sx={{ color: "common.white" }} onClick={onClose}>
              <LuChevronLeft size={25} />
            </IconButton>
            <Typography variant="h3" sx={{ color: "common.white" }}>
              Thanh toán
            </Typography>
          </Stack>
        </AppbarBase>
        <Box>
          <Container maxWidth={MOBILE_CONTAINER}>
            <Stack
              gap={2}
              className="hidden-scroll"
              sx={{
                height: `calc(100vh - 60px - 45px)`,
                py: 2,
                overflow: "auto",
              }}
            >
              <TotalLine
                label="Tiền hàng"
                count={currentHd?.details?.length}
                value={currentHd?.t_tien_nt}
              />
              <TotalLine
                label="Giảm giá sản phẩm"
                count={giamGia.so_luong}
                value={giamGia.gia_tri}
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography sx={{ fontWeight: 600 }}>Giảm giá</Typography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Button
                    onClick={() => setIsPercent(!isPercent)}
                    size="small"
                    variant="contained"
                    sx={{
                      boxShadow: "none",
                      px: 1,
                      py: 0.5,
                      fontSize: "12px",
                      fontWeight: 400,
                      textTransform: "none",
                      minWidth: "unset",
                      color: "common.white",
                      width: "42px",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    {isPercent ? "%" : "VND"}
                  </Button>
                  {isPercent ? (
                    <TextField
                      type="number"
                      variant="standard"
                      autoComplete="off"
                      value={giaTriChietKhau.ty_le_ck}
                      onChange={handleGiaTriChietKhauChange}
                      sx={{
                        "& .MuiInputBase-input": {
                          textAlign: "right",
                          width: "80px",
                        },
                      }}
                      onBlur={handleSaveCk}
                      onKeyUp={(e) => {
                        if (e.which === 13) {
                          handleSaveCk();
                        }
                      }}
                    />
                  ) : (
                    <TextField
                      variant="standard"
                      autoComplete="off"
                      value={numeralCustom(giaTriChietKhau.tien_ck).format()}
                      onChange={handleGiaTriChietKhauChange}
                      onBlur={handleSaveCk}
                      slotProps={{ input: { inputMode: "numeric" } }}
                      onKeyUp={(e) => {
                        if (e.which === 13) {
                          handleSaveCk();
                        }
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          textAlign: "right",
                          width: "80px",
                        },
                      }}
                    />
                  )}

                  <Typography>đ</Typography>
                </Stack>
              </Stack>
              <Stack
                gap={2}
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  border: "1px dashed",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontWeight: 600 }}>Nhập mã vé</Typography>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <TextField
                      variant="standard"
                      autoComplete="off"
                      value={maVoucher}
                      onChange={(e) => setMaVoucher(e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          textAlign: "right",
                          width: "120px",
                        },
                      }}
                    />
                    <Button
                      disabled={!maVoucher}
                      onClick={applyVoucher}
                      size="small"
                      variant="contained"
                      sx={{
                        boxShadow: "none",
                        px: 1,
                        py: 0.5,
                        fontSize: "12px",
                        fontWeight: 400,
                        textTransform: "none",
                        minWidth: "unset",
                        color: "common.white",
                        width: "80px",
                        "&:hover": { boxShadow: "none" },
                      }}
                    >
                      Áp dụng
                    </Button>
                  </Stack>
                </Stack>
                <TotalLine label="Giảm giá" value={currentHd?.tien_evoucher} />
                {isArray(currentHd?.evouchers) &&
                  currentHd?.evouchers?.length > 0 && (
                    <Stack gap={1}>
                      {currentHd?.evouchers?.map((ev: any, index: number) => {
                        return (
                          <MenuItem
                            key={ev.ma}
                            sx={{ justifyContent: "space-between" }}
                          >
                            <Typography>
                              {index + 1}. {ev.ma} - {ev.ten}
                            </Typography>
                            <IconButton onClick={() => deleteVoucher(ev?.ma)}>
                              <LuXCircle size={20} />
                            </IconButton>
                          </MenuItem>
                        );
                      })}
                    </Stack>
                  )}
              </Stack>
              <TotalLine
                label="Khách phải trả"
                value={currentHd?.t_tt_nt}
                valueSx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "error.main",
                }}
              />
              <TienThu tongTien={currentHd?.t_tt_nt || 0} />
              <TotalLine
                label="Tiền thừa"
                value={currentHd?.phai_tra}
                valueSx={{
                  color: "secondary.main",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              />
              <Pttt />
            </Stack>
          </Container>
        </Box>
        <Box>
          <Container maxWidth="sm">
            <Button
              fullWidth
              onClick={handlePayment}
              variant="contained"
              color="primary"
              sx={{
                height: "45px",
                borderRadius: "0px",
                boxShadow: "none",
                color: "common.white",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Thanh toán
            </Button>
          </Container>
        </Box>
      </Box>
    </Drawer>
  );
}

export default Payment;
